import React, { useMemo } from 'react';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { getThemeColor } from '../ProgressionNiveau/SequenceNode';
import MathText from '../../VueQuestion/MathText';
import { tronquerTexteMaths } from '../../../utils/tronquerTexteMaths';

// Configuration des types d'activités (QFLASH, AUTOMATISME, RESOLUTION) - couleurs pour fond clair
const ACTIVITY_CONFIGS = {
  AUTOMATISME: {
    icon: "⚡",
    bg: "bg-emerald-100",
    bgSolid: "bg-emerald-500",
    border: "border-emerald-400",
    text: "text-emerald-800",
    textDark: "text-emerald-700",
    label: "Automatisme",
    labelPlural: "Automatismes"
  },
  RESOLUTION: {
    icon: "🧩",
    bg: "bg-rose-100",
    bgSolid: "bg-rose-500",
    border: "border-rose-400",
    text: "text-rose-800",
    textDark: "text-rose-700",
    label: "Résolution",
    labelPlural: "Résolutions de problèmes"
  },
  QFLASH: {
    icon: "💡",
    bg: "bg-amber-100",
    bgSolid: "bg-amber-500",
    border: "border-amber-400",
    text: "text-amber-800",
    textDark: "text-amber-700",
    label: "QFlash",
    labelPlural: "QFlash"
  },
};

// Composant badge compteur pour les activités (cliquable) - adapté pour fond clair
const ActivityCountBadge = ({ type, count, onClick }) => {
  const config = ACTIVITY_CONFIGS[type] || ACTIVITY_CONFIGS.AUTOMATISME;

  return (
    <button
      onClick={onClick}
      className={`
        flex items-center gap-1.5 px-3 py-1.5 rounded-lg
        ${config.bg} border-2 ${config.border} ${config.text}
        text-xs font-bold hover:scale-105 transition-all duration-200
        hover:shadow-lg cursor-pointer shadow-sm
      `}
    >
      <span>{config.icon}</span>
      <span className="font-black">{count}</span>
    </button>
  );
};

// getThemeColor est IMPORTÉ de SequenceNode (source unique) : la copie locale
// qui vivait ici avait déjà divergé (thèmes de français et du cycle 4 rénové
// absents, recherche sensible à la casse). Une seule table à maintenir.

// Fonction pour formater les dates
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

// Fonction pour calculer la durée en semaines
const calculateDuration = (start, end) => {
  if (!start || !end) return '';
  const startDate = new Date(start);
  const endDate = new Date(end);
  const diffTime = Math.abs(endDate - startDate);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const weeks = Math.ceil(diffDays / 7);
  return `${weeks} semaine${weeks > 1 ? 's' : ''}`;
};

// Fonction pour calculer le pourcentage d'acquisition pour un item.
// Exportée : le panneau détail partagé (PanneauContenus) s'en sert côté
// classe pour afficher l'acquisition des séquences chargées (mode pill).
export const calculateAcquisitionStats = (item, type, eleves) => {


  if (!eleves || eleves.length === 0) return null;

  const titleToMatch = type === 'savoir-faire' ? item.nom : item.description;
  if (!titleToMatch) return null;

  
  let studentsWithResults = 0;
  let studentsAcquired = 0;

  eleves.forEach(eleve => {
    if (!eleve.resultats || eleve.resultats.length === 0) return;

    // Trouver les résultats qui matchent
    const matchingResults = eleve.resultats.filter(resultat => {
      if (type === 'savoir-faire') {
        return resultat.savoirTitre === titleToMatch;
      } else {
        return resultat.attenduTitre === titleToMatch;
      }
    });

  
    if (matchingResults.length > 0) {
      // Filtrer les résultats avec des scores non null
      const resultsWithScores = matchingResults.filter(r => r.scores !== null && r.scores !== undefined);

      if (resultsWithScores.length > 0) {
        studentsWithResults++;
        // Calculer la moyenne des scores (uniquement ceux non null)
        const totalScore = resultsWithScores.reduce((sum, r) => sum + r.scores, 0);
        const averageScore = totalScore / resultsWithScores.length;
        // Si moyenne >= 5, considéré comme acquis
        if (averageScore >= 5) {
          studentsAcquired++;
        }
      }
    }
  });
  if (studentsWithResults === 0) return null;
  return {
    percentage: Math.round((studentsAcquired / studentsWithResults) * 100),
    acquired: studentsAcquired,
    total: studentsWithResults
  };
};

// Troncature sûre pour le LaTeX : utilitaire partagé
const truncateText = tronquerTexteMaths;

// Composant pour afficher un savoir-faire ou attendu (comme ContentNode)
const ContentNodeConsultation = ({ item, isActive, side, type, acquisitionStats }) => {
  const isSavoirFaire = type === 'savoir-faire';
  const fullText = item.description || item.nom || item;
  const { truncated, isTruncated } = truncateText(fullText, 100);

  return (
    <div className={`flex items-center gap-3 ${side === 'left' ? 'flex-row-reverse' : ''}`}>
      {/* Ligne de connexion */}
      <div className="relative">
        <div className="w-8 h-0.5 bg-slate-300" />
        <div className={`absolute ${side === 'left' ? '-left-1' : '-right-1'} top-1/2 -translate-y-1/2 w-2 h-2 rounded-full ${isActive ? (isSavoirFaire ? 'bg-emerald-400' : 'bg-blue-400') : 'bg-slate-400'}`} />
      </div>

      {/* Carte du contenu */}
      <div className={`
        min-w-56 max-w-72 p-3 rounded-xl border-2 transition-all duration-300
        ${isActive
          ? isSavoirFaire
            ? 'bg-emerald-50 border-emerald-200 shadow-md'
            : 'bg-blue-50 border-blue-200 shadow-md'
          : 'bg-white/60 border-slate-200'
        }
      `}>
        {/* Badge type */}
        <span className={`
          inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold mb-2
          ${isSavoirFaire
            ? 'bg-emerald-100 text-emerald-700'
            : 'bg-blue-100 text-blue-700'
          }
        `}>
          {isSavoirFaire ? '💡 Savoir-faire' : '📘 Attendu'}
        </span>

        {/* Description avec troncature et tooltip custom */}
        <div className="relative group">
          <p
            className={`text-sm font-medium leading-relaxed ${isActive ? 'text-slate-700' : 'text-slate-500'} ${isTruncated ? 'cursor-help' : ''}`}
          >
            <MathText>{truncated}</MathText>
          </p>
          {/* Tooltip custom instantané */}
          {isTruncated && (
            <div className={`
              absolute z-[9999] bottom-full left-1/2 -translate-x-1/2 mb-2
              px-3 py-2 max-w-xs w-max
              bg-slate-800 text-white text-xs leading-relaxed rounded-lg shadow-xl
              opacity-0 invisible group-hover:opacity-100 group-hover:visible
              transition-opacity duration-150
              pointer-events-none
            `}>
              <MathText>{fullText}</MathText>
              {/* Flèche du tooltip */}
              <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
            </div>
          )}
        </div>

        {/* Barre de progression d'acquisition */}
        {acquisitionStats && (
          <div className="mt-3">
            <div className="flex justify-between items-center text-[10px] mb-1">
              <span className="text-slate-500">Acquisition</span>
              <span className={`font-bold ${
                acquisitionStats.percentage >= 75 ? 'text-emerald-600' :
                acquisitionStats.percentage >= 50 ? 'text-amber-600' :
                'text-red-500'
              }`}>
                {acquisitionStats.percentage}% ({acquisitionStats.acquired}/{acquisitionStats.total})
              </span>
            </div>
            <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  acquisitionStats.percentage >= 75 ? 'bg-gradient-to-r from-emerald-400 to-emerald-500' :
                  acquisitionStats.percentage >= 50 ? 'bg-gradient-to-r from-amber-400 to-amber-500' :
                  'bg-gradient-to-r from-red-400 to-red-500'
                }`}
                style={{ width: `${acquisitionStats.percentage}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Composant pour une carte de séquence en consultation (style SequenceNode)
const SequenceCardConsultation = ({ sequence, index, isVu, onToggleVu, isLoading, activitesParalleles, classeDetail,
   onViewActivities, onViewContenus }) => {
  // Grouper les activités par type et compter
  const activityCounts = useMemo(() => {
    if (!activitesParalleles || activitesParalleles.length === 0) return {};

    const counts = {};
    activitesParalleles.forEach(act => {
      const type = act.type || 'AUTOMATISME';
      if (!counts[type]) {
        counts[type] = { count: 0, activities: [] };
      }
      counts[type].count++;
      counts[type].activities.push(act);
    });
    return counts;
  }, [activitesParalleles]);

  const hasActivities = Object.keys(activityCounts).length > 0;

  const handleActivityBadgeClick = (e, type) => {
    e.stopPropagation();
    if (onViewActivities) {
      onViewActivities(type, activityCounts[type]?.activities || [], sequence.title);
    }
  };
  // Simplifié : Vue = vert (completed), Non vue = orange (style "en cours") avec label "À venir"
  const isCompleted = isVu;
  const isAVenir = !isVu;
  const themeColors = getThemeColor(sequence.theme_for_sequence);

  const handleToggleVu = (e) => {
    e.stopPropagation();
    if (onToggleVu) {
      onToggleVu(sequence.id, !isVu);
    }
  };

  // Fusionner savoir-faires et attendus avec un type pour chacun
  const savoirFaires = (sequence.savoir_faires_details || []).map(sf => ({ ...sf, _type: 'savoir-faire' }));
  const attendus = (sequence.attendus_details || []).map(att => ({ ...att, _type: 'attendu' }));
  const allContent = [...savoirFaires, ...attendus];

  // Au-delà de 4 contenus, les satellites déforment la frise : même règle
  // que SequenceNode côté niveau — mode PILL, le compteur cliquable ouvre
  // le panneau détail partagé (PanneauContenus).
  const modePill = allContent.length > 4 && !!onViewContenus;

  // Calculer les stats d'acquisition pour chaque item
  const eleves = classeDetail?.eleves || [];

  // Alterne gauche/droite
  const leftItems = allContent.filter((_, i) => i % 2 === 0);
  const rightItems = allContent.filter((_, i) => i % 2 === 1);

  return (
    <div className="relative">
      {/* Barre d'activités parallèles - compteurs groupés */}
      {hasActivities && (
        <div className="absolute -top-10 left-1/2 -translate-x-1/2 flex gap-2 z-20 flex-wrap justify-center items-center">
          {/* Badges compteurs par type */}
          {Object.entries(activityCounts).map(([type, data]) => (
            <ActivityCountBadge
              key={type}
              type={type}
              count={data.count}
              onClick={(e) => handleActivityBadgeClick(e, type)}
            />
          ))}
        </div>
      )}

      <div className="flex flex-col lg:flex-row items-center lg:items-start justify-center gap-4">
        {/* Contenus GAUCHE (savoir-faires + attendus) - Desktop uniquement */}
        <div className="hidden lg:flex flex-col gap-3 items-end w-72 pt-4">
          {!modePill && leftItems.map((item, idx) => (
            <ContentNodeConsultation
              key={item.id || idx}
              item={item}
              isActive={true}
              side="left"
              type={item._type}
              acquisitionStats={calculateAcquisitionStats(item, item._type, eleves)}
            />
          ))}
        </div>

        {/* SÉQUENCE principale */}
        <div className="relative z-10">
          {/* Glow effect pour les séquences non vues (à venir) */}
          {isAVenir && (
            <div className="absolute inset-0 -m-3 rounded-3xl bg-gradient-to-r from-orange-400/30 via-amber-400/30 to-orange-400/30 blur-xl animate-pulse" />
          )}

          {/* Points de connexion */}
          <div className={`
            absolute top-1/2 -left-4 w-3 h-3 rounded-full -translate-y-1/2 border-2 border-white
            ${isCompleted ? 'bg-emerald-500 shadow-lg shadow-emerald-500/50' : 'bg-orange-500 shadow-lg shadow-orange-500/50'}
          `} />
          <div className={`
            absolute top-1/2 -right-4 w-3 h-3 rounded-full -translate-y-1/2 border-2 border-white
            ${isCompleted ? 'bg-emerald-500 shadow-lg shadow-emerald-500/50' : 'bg-orange-500 shadow-lg shadow-orange-500/50'}
          `} />

          {/* Card principale */}
          <div className={`
            relative w-full sm:w-[420px] p-4 sm:p-6 rounded-2xl transition-all duration-500
            backdrop-blur-xl bg-white/95 border-2 border-white/70 shadow-2xl
            ${isCompleted ? 'ring-2 ring-emerald-400/50' : 'ring-2 ring-orange-400/70'}
          `}>
            {/* Numéro séquence */}
            <div className={`
              absolute -top-4 -left-4 w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center
              font-black text-base sm:text-lg border-2 border-white shadow-lg
              ${isCompleted
                ? 'bg-gradient-to-br from-emerald-400 to-emerald-600 text-white shadow-emerald-500/40'
                : 'bg-gradient-to-br from-orange-400 to-orange-600 text-white shadow-orange-500/40'}
            `}>
              {isCompleted ? (
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                `S${index + 1}`
              )}
            </div>

            {/* Status badge */}
            <div className="absolute -top-2 right-4 flex items-center gap-2">
              <span className={`
                inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider text-white shadow-lg
                ${isCompleted
                  ? 'bg-gradient-to-r from-emerald-400 to-emerald-500 shadow-emerald-500/40'
                  : 'bg-gradient-to-r from-orange-400 to-orange-500 shadow-orange-500/40 animate-pulse'}
              `}>
                {isAVenir && <span className="w-1.5 h-1.5 bg-white rounded-full animate-ping" />}
                {isCompleted && <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                {isCompleted ? 'Vue' : 'À voir'}
              </span>
            </div>

            {/* Contenu */}
            <div className="mt-4">
              {/* Thème */}
              <span className={`text-xs font-medium uppercase tracking-wider ${themeColors.text}`}>
                {sequence.theme_for_sequence}
              </span>

              {/* Titre */}
              <h3 className="text-lg sm:text-xl font-bold mt-1 mb-3 text-slate-800">
                {sequence.title}
              </h3>

              {/* Estimation + Dates et durée */}
              <div className="mb-4">
                <p className="text-[10px] italic mb-1 text-slate-500">
                  Estimation
                </p>
                <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs">
                  <div className="flex items-center gap-1.5 text-blue-600">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="font-semibold">{formatDate(sequence.start)} → {formatDate(sequence.end)}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-purple-600">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-semibold">{calculateDuration(sequence.start, sequence.end)}</span>
                  </div>
                </div>
              </div>

              {/* Mode PILL (> 4 contenus) : le compteur cliquable ouvre le
                  panneau détail partagé — la frise ne se déforme jamais */}
              {modePill && (
                <div className="flex flex-col items-center gap-1 mb-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); onViewContenus(sequence); }}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border-2 border-blue-300 text-blue-600 text-[13px] font-extrabold hover:bg-blue-100 hover:border-blue-400 hover:shadow-md transition-all"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                    </svg>
                    {allContent.length} savoirs et attendus
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 18l6-6-6-6" />
                    </svg>
                  </button>
                  <p className="text-[10px] text-slate-400">le détail s'ouvre dans un panneau</p>
                </div>
              )}

              {/* Stats savoir-faires et attendus - seulement si il y en a
                  (en mode pill, la pill porte le compteur) */}
              {allContent.length > 0 && !modePill && (
                <div className="mb-3">
                  <div className="flex justify-between text-[10px] text-slate-500 mb-1 font-medium">
                    <span>{savoirFaires.length} savoir-faire{savoirFaires.length > 1 ? 's' : ''}</span>
                    {attendus.length > 0 && (
                      <span>{attendus.length} attendu{attendus.length > 1 ? 's' : ''}</span>
                    )}
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-1000 ${
                        isCompleted
                          ? 'bg-gradient-to-r from-emerald-400 to-emerald-500'
                          : 'bg-gradient-to-r from-orange-400 to-amber-500'
                      }`}
                      style={{ width: isCompleted ? '100%' : '50%' }}
                    />
                  </div>
                </div>
              )}

              {/* Message si pas de contenu */}
              {allContent.length === 0 && (
                <div className="text-center py-2">
                  <p className="text-slate-400 text-xs italic">
                    Aucun savoir-faire ou attendu associé
                  </p>
                </div>
              )}

              {/* Bouton Vu / Pas vu */}
              <button
                onClick={handleToggleVu}
                disabled={isLoading}
                className={`
                  w-full mt-3 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300
                  flex items-center justify-center gap-2
                  ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}
                  ${isVu
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200'
                  }
                `}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Mise à jour...</span>
                  </>
                ) : isVu ? (
                  <>
                    <Eye className="w-4 h-4" />
                    <span>Séquence vue</span>
                  </>
                ) : (
                  <>
                    <EyeOff className="w-4 h-4" />
                    <span>Marquer comme vue</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Contenus DROITE (savoir-faires + attendus) - Desktop uniquement */}
        <div className="hidden lg:flex flex-col gap-3 items-start w-72 pt-4">
          {!modePill && rightItems.map((item, idx) => (
            <ContentNodeConsultation
              key={item.id || idx}
              item={item}
              isActive={true}
              side="right"
              type={item._type}
              acquisitionStats={calculateAcquisitionStats(item, item._type, eleves)}
            />
          ))}
        </div>

        {/* Contenus MOBILE (tous les savoir-faires + attendus en dessous) */}
        <div className="lg:hidden flex flex-col gap-3 w-full max-w-sm px-4 mt-4">
          {!modePill && allContent.map((item, idx) => (
            <ContentNodeConsultation
              key={item.id || idx}
              item={item}
              isActive={true}
              side="bottom"
              type={item._type}
              acquisitionStats={calculateAcquisitionStats(item, item._type, eleves)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default SequenceCardConsultation;
